import React, { useCallback, useEffect, useState } from 'react';
import { cryptoPortfolioService } from '../../services/cryptoPortfolioService';

export interface ActivityBotAiTunePerformanceSnapshotProps {
  botId: number;
  active: boolean;
}

const ActivityBotAiTunePerformanceSnapshot: React.FC<ActivityBotAiTunePerformanceSnapshotProps> = ({
  botId,
  active,
}) => {
  const [window, setWindow] = useState('12');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [evaluation, setEvaluation] = useState<Record<string, unknown> | null>(null);

  const load = useCallback(async () => {
    if (!botId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await cryptoPortfolioService.getActivityBotAiPerformanceSnapshot(botId, window);
      setEvaluation((res.evaluation as Record<string, unknown>) ?? null);
    } catch (e: unknown) {
      setEvaluation(null);
      setError(e instanceof Error ? e.message : 'Failed to load performance snapshot');
    } finally {
      setLoading(false);
    }
  }, [botId, window]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  if (loading) return <p style={{ fontSize: 13, color: '#6b7280' }}>Loading performance…</p>;
  if (error) return <p style={{ fontSize: 13, color: '#dc2626' }}>{error}</p>;
  if (!evaluation) {
    return (
      <div style={{ padding: 16, background: '#f9fafb', borderRadius: 8, color: '#6b7280', fontSize: 13 }}>
        No closed legs yet for performance evaluation.
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <label style={{ fontSize: 12, fontWeight: 600 }}>Window:</label>
        <select value={window} onChange={e => setWindow(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e5e7eb' }}>
          <option value="5">Last 5</option>
          <option value="10">Last 10</option>
          <option value="12">Last 12</option>
          <option value="20">Last 20</option>
          <option value="all">All loaded</option>
        </select>
        <button type="button" onClick={() => void load()} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e5e7eb' }}>Refresh</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
        {[
          ['Trades', evaluation.tradeCount],
          ['Net P&L', evaluation.netPnl],
          ['Win rate', `${evaluation.winRate}%`],
          ['Wins', evaluation.wins],
          ['Losses', evaluation.losses],
        ].map(([label, value]) => (
          <div key={String(label)} style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f8fafc' }}>
            <div style={{ fontSize: 11, color: '#64748b' }}>{String(label)}</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>{value == null ? '—' : String(value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityBotAiTunePerformanceSnapshot;
